import { toApiError } from './api-error.ts'
import { api } from './api.ts'

/** One merchant organization in the directory. */
export interface Merchant {
  /** The organization's id. What every merchant filter in this console names. */
  orgId: number
  /** Null when the organization row holds no name. Two merchants can share a name. */
  orgName: string | null
  /** Whether the organization is still active. A picker may hide the rest. */
  isActive: boolean
}

export interface MerchantDirectory {
  merchantCount: number
  /** How many of them are active. Counted from the same rows the list holds. */
  activeCount: number
  /** Every merchant, ordered by name then by id. */
  merchants: Merchant[]
}

/** What a picker shows for a merchant whose organization row carries no name. */
export const UNNAMED_MERCHANT = 'Unnamed merchant'

/**
 * Read the merchant directory.
 *
 * The whole directory arrives in one answer — a few hundred rows — and the
 * browser filters it. `GET /merchants` takes no parameters, so there is nothing
 * to send and nothing that can be sent wrong.
 *
 * @returns Every merchant organization.
 */
export async function fetchMerchants(): Promise<MerchantDirectory> {
  try {
    return await api.get('merchants').json<MerchantDirectory>()
  } catch (error) {
    throw await toApiError(error)
  }
}

/**
 * What a merchant is called on screen.
 *
 * One function, so a merchant reads the same in a picker, in a chip and in a
 * report's own heading. A missing name is named rather than left blank: a row
 * with no label is one a reader cannot tell from a rendering fault.
 *
 * @param merchant - The merchant, or undefined when the directory has no entry for the id.
 * @returns The label to print.
 */
export function merchantLabel(merchant: Merchant | undefined): string {
  return merchant?.orgName ?? UNNAMED_MERCHANT
}

/**
 * Turn a list of merchant ids into the `orgIds` query parameter.
 *
 * Empty answers an empty string, which every endpoint reading this parameter
 * treats as "every merchant" rather than "no merchant" — so a caller sends the
 * same parameter whether or not a filter is in force.
 *
 * @param orgIds - The merchants selected.
 * @returns The comma-separated value to send.
 */
export function toOrgIdsParam(orgIds: readonly number[]): string {
  return orgIds.join(',')
}

/**
 * Read the `orgIds` query parameter back into a list of merchant ids.
 *
 * The counterpart to `toOrgIdsParam`, for a screen that keeps its selection in
 * the URL rather than in React state. Anything that is not a whole number is
 * dropped rather than passed on as `NaN`: a hand-edited URL should narrow to
 * the merchants it does name, not fail the picker that renders it.
 *
 * @param value - The parameter as the URL carries it, or undefined when absent.
 * @returns The merchants selected. Empty means every merchant.
 */
export function fromOrgIdsParam(value: string | undefined): number[] {
  return (value ?? '')
    .split(',')
    .map(part => Number(part.trim()))
    .filter(orgId => Number.isInteger(orgId) && orgId > 0)
}

/**
 * Index the directory by id, for a screen that has an id and needs a name.
 *
 * @param merchants - The directory's rows, or undefined while it loads.
 * @returns A lookup from `orgId` to merchant.
 */
export function merchantsById(
  merchants: readonly Merchant[] | undefined
): Map<number, Merchant> {
  return new Map((merchants ?? []).map(merchant => [merchant.orgId, merchant]))
}
